import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import {
  ALL_GRANTED_PREFS,
  DEFAULT_DENIED_PREFS,
  pushConsentUpdate,
  readStoredConsent,
  writeStoredConsent,
  clearStoredConsent,
  type ConsentCategory,
  type CookiePreferences,
} from "@/lib/consent";
import { loadGoogleAnalytics, loadGoogleTagManager } from "@/lib/analytics";
import { flushTrackingQueue } from "@/lib/track";

export type { CookiePreferences } from "@/lib/consent";

interface CookieConsentContextType {
  preferences: CookiePreferences;
  hasConsent: (type: Exclude<ConsentCategory, "necessary">) => boolean;
  acceptAll: () => void;
  acceptNecessary: () => void;
  savePreferences: (preferences: CookiePreferences) => void;
  withdrawConsent: () => void;
  showBanner: boolean;
  setShowBanner: (show: boolean) => void;
  openPreferences: () => void;
  consentTimestamp: number | null;
}

const CookieConsentContext = createContext<CookieConsentContextType | null>(
  null,
);

/**
 * Side-effects to fire when consent changes — load (or skip) third-party
 * Google scripts according to the new state. Idempotent: the loaders
 * themselves bail out on the second call.
 */
function applySideEffects(prefs: CookiePreferences): void {
  pushConsentUpdate(prefs);
  if (prefs.analytics) {
    loadGoogleAnalytics();
    // GTM ships analytics + ads tags; only load it when the user has at
    // least granted analytics. Marketing tags inside GTM stay gated by
    // the ad_storage signal regardless.
    loadGoogleTagManager();
  }
  // Drain (or drop) any tracking events captured before the user made
  // a consent decision. Cheap no-op when the queue is empty.
  flushTrackingQueue();
}

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [preferences, setPreferences] =
    useState<CookiePreferences>(DEFAULT_DENIED_PREFS);
  const [showBanner, setShowBanner] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [consentTimestamp, setConsentTimestamp] = useState<number | null>(null);
  const sideEffectsAppliedRef = useRef(false);

  // Hydrate from storage. If a record exists and is still within the 12-month
  // TTL the banner stays hidden; otherwise we re-prompt.
  useEffect(() => {
    const stored = readStoredConsent();
    if (stored) {
      setPreferences(stored.prefs);
      setConsentTimestamp(stored.timestamp);
      setShowBanner(false);
      // Re-apply side-effects so GA/GTM load on every page-load when
      // consent is already granted (defaults in index.html still say denied).
      applySideEffects(stored.prefs);
      sideEffectsAppliedRef.current = true;
    } else {
      setShowBanner(true);
    }
    setIsLoaded(true);
  }, []);

  const hasConsent = useCallback(
    (type: Exclude<ConsentCategory, "necessary">) => preferences[type],
    [preferences],
  );

  const savePreferences = useCallback((next: CookiePreferences) => {
    const prefs: CookiePreferences = { ...next, necessary: true };
    const record = writeStoredConsent(prefs);
    setPreferences(prefs);
    setConsentTimestamp(record.timestamp);
    setShowBanner(false);
    applySideEffects(prefs);
    sideEffectsAppliedRef.current = true;
  }, []);

  const acceptAll = useCallback(() => {
    savePreferences(ALL_GRANTED_PREFS);
  }, [savePreferences]);

  const acceptNecessary = useCallback(() => {
    savePreferences(DEFAULT_DENIED_PREFS);
  }, [savePreferences]);

  const withdrawConsent = useCallback(() => {
    clearStoredConsent();
    setPreferences(DEFAULT_DENIED_PREFS);
    setConsentTimestamp(null);
    pushConsentUpdate(DEFAULT_DENIED_PREFS);
    flushTrackingQueue();
    setShowBanner(true);
  }, []);

  const openPreferences = useCallback(() => {
    setShowBanner(true);
  }, []);

  const contextValue: CookieConsentContextType = {
    preferences,
    hasConsent,
    acceptAll,
    acceptNecessary,
    savePreferences,
    withdrawConsent,
    showBanner: isLoaded ? showBanner : false,
    setShowBanner,
    openPreferences,
    consentTimestamp,
  };

  return (
    <CookieConsentContext.Provider value={contextValue}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error(
      "useCookieConsent must be used within a CookieConsentProvider",
    );
  }
  return context;
}
